import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ConnectionDetail } from "@typedoc/contracts/connections";
import { Button, Input } from "@typedoc/ui";
import { ErrorNotice } from "../error-notice";
import { normalizeAppError } from "../../lib/app-error";
import { type MembersPersonalOption, updateLabel } from "../../lib/connections/api";
import type { ConnectionsContext } from "../../lib/connections/use-connections-scope";
import { connectionQueryOptions, connectionsKey } from "../../lib/queries/connections";

/** The server's limit (`labelInputSchema`), so the field never accepts more than can be saved. */
const MAX_LENGTH = 80;

/**
 * The account's name, renamed where it stands. Whoever manages the account clicks it to edit;
 * Enter or leaving the field saves, Escape puts it back, and an empty or unchanged name saves
 * nothing. The save carries the revision the name was read at, so a rename that crosses someone
 * else's change is refused and offers a reload rather than overwriting it.
 */
export function EditableName({
  connection,
  context,
  editable,
  options,
}: {
  connection: ConnectionDetail;
  context: ConnectionsContext;
  editable: boolean;
  /** How the detail was read, so the renamed account lands under the key the screen shows. */
  options?: MembersPersonalOption | undefined;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: (label: string) =>
      updateLabel(context, connection.id, {
        revision: connection.revision,
        label,
      }),
    onSuccess: async (next) => {
      queryClient.setQueryData(
        connectionQueryOptions(context, connection.id, options).queryKey,
        next,
      );
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: connectionsKey(context.scope) });
    },
  });
  const problem = rename.error ? normalizeAppError(rename.error) : null;

  function start() {
    rename.reset();
    setDraft(connection.label);
  }

  function finish() {
    if (draft === null || rename.isPending) return;
    const label = draft.trim();
    if (!label || label === connection.label) {
      setDraft(null);
      rename.reset();
      return;
    }
    rename.mutate(label);
  }

  if (!editable) return <h2 className="connection-title break-words">{connection.label}</h2>;

  return (
    <>
      {draft === null ? (
        <h2 className="connection-title group relative flex max-w-full items-center gap-1.5 break-words">
          <span className="-mx-1.5 min-w-0 rounded-md px-1.5 transition-colors duration-fast ease-out group-hover:bg-secondary motion-reduce:transition-none">
            {connection.label}
          </span>
          <button
            type="button"
            aria-label="Rename"
            onClick={start}
            // Its hit area stretches over the whole heading, so clicking the name edits it too.
            className="grid size-7 shrink-0 cursor-text place-items-center after:absolute after:inset-0 after:content-[''] rounded-md text-subtle-foreground opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100 motion-reduce:transition-none max-sm:opacity-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M10.5 3.5l2 2M3 13l.6-2.6L10.9 3.1a1 1 0 0 1 1.4 0l.6.6a1 1 0 0 1 0 1.4L5.6 12.4 3 13z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </h2>
      ) : (
        <Input
          // Focus follows the click that opened it, the way a heading turns into its field.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          aria-label="Account name"
          // The design system's field at the heading's size: its 1px border and 8px padding are
          // pulled back out, so the words sit exactly where the heading's did.
          className="connection-name-input -mx-[9px] h-auto w-[calc(100%+18px)] max-w-none px-2 py-px selection:bg-ring/15"
          value={draft}
          maxLength={MAX_LENGTH}
          readOnly={rename.isPending}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={finish}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              finish();
            } else if (event.key === "Escape") {
              event.preventDefault();
              setDraft(null);
              rename.reset();
            }
          }}
        />
      )}
      {problem && (
        <div className="mt-2 flex flex-col items-start gap-2">
          <ErrorNotice error={problem} className="w-full" />
          {problem.code === "CONFLICT" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft(null);
                rename.reset();
                void queryClient.invalidateQueries({ queryKey: connectionsKey(context.scope) });
              }}
            >
              Reload
            </Button>
          )}
        </div>
      )}
    </>
  );
}
